import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { BookOpen, Mic, Film, ArrowLeft, FileText, Upload, UserRound, ChevronDown, Check } from 'lucide-react';
import DOMPurify from 'dompurify';
import api from '../../services/api';
import { ContentSearchResult, CreatePostData, ArticleMetadata, PDFFile } from '../../types';
import './Create.css';
import '../../universal.css';
import UniversalToast from '../../components/UniversalToast';

// Decode common HTML entities for titles/authors coming from external sources
const decodeHtmlEntities = (input: string): string => {
  if (!input) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = input;
  return textarea.value;
};

// Convert @username to <a data-mention="username">@username</a> (lowercase in data-mention)
const linkifyMentions = (htmlOrText: string): string => {
  if (!htmlOrText) return '';
  // Replace plain @mentions; keep leading space or start-of-string
  return htmlOrText.replace(/(^|\s)@([A-Za-z0-9_.]+)/g, (full, lead, uname) => {
    const lower = (uname || '').toLowerCase();
    return `${lead}<a data-mention="${lower}">@${uname}</a>`;
  });
};

// Helper function to sanitize HTML while preserving bold, italic, line breaks and mention anchors
const sanitizePostHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['br', 'strong', 'em', 'div', 'a'],
    ALLOWED_ATTR: ['data-mention']
  });
};

type ContentType = 'book' | 'podcast' | 'movie' | 'article' | 'pdf';
type Step = 'search' | 'create';

const Create: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('search');
  const [contentType, setContentType] = useState<ContentType>('book');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Existing content search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ContentSearchResult[]>([]);
  const [selectedContent, setSelectedContent] = useState<ContentSearchResult | null>(null);


  // Article URL state
  const [articleUrl, setArticleUrl] = useState('');
  const [articleMetadata, setArticleMetadata] = useState<ArticleMetadata | null>(null);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const [showManualArticleInput, setShowManualArticleInput] = useState(false);
  const [manualArticleTitle, setManualArticleTitle] = useState('');

  // PDF upload state
  const [pdfFile, setPdfFile] = useState<PDFFile | null>(null);
  const [isUploadingPdf] = useState(false);

  // Movie search state
  const [movieResults, setMovieResults] = useState<any[]>([]);
  const [selectedMovie, setSelectedMovie] = useState<any | null>(null);

  // Refs for auto-focus
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const articleInputRef = React.useRef<HTMLInputElement>(null);
  const createTextareaRef = React.useRef<HTMLDivElement>(null);

  // Common state
  const [postContent, setPostContent] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showLoadingToast, setShowLoadingToast] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPosting, setIsPosting] = useState(false);


  // Get user from localStorage
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });

  // Update user data when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const updatedUser = JSON.parse(localStorage.getItem('user') || '{}');
        setUser(updatedUser);
      } catch {
        setUser({});
      }
    };

    // Listen for storage changes
    window.addEventListener('storage', handleStorageChange);

    // Also check for changes periodically (in case localStorage is updated in same tab)
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Initialize empty class for placeholder
  useEffect(() => {
    if (createTextareaRef.current) {
      if (postContent.trim() === '') {
        createTextareaRef.current.classList.add('empty');
      } else {
        createTextareaRef.current.classList.remove('empty');
      }
    }
  }, [postContent]);

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    // Reset height to auto to get the natural height
    textarea.style.height = 'auto';

    // Calculate the new height based on content
    const newHeight = textarea.scrollHeight;
    textarea.style.height = newHeight + 'px';

    // Ensure minimum height
    const minHeight = 40; // Minimum height in pixels
    if (newHeight < minHeight) {
      textarea.style.height = minHeight + 'px';
    }

    // Let the textarea grow as much as needed - the page will scroll
  };

  // Auto-resize textarea when step changes to 'create'
  useEffect(() => {
    if (step === 'create') {
      const textarea = document.getElementById('content') as HTMLTextAreaElement;
      if (textarea) {
        autoResizeTextarea(textarea);
      }
      // Auto-focus the textarea
      if (createTextareaRef.current) {
        createTextareaRef.current.focus();
      }
    }
  }, [step]);

  // Handle window resize to recalculate textarea height
  useEffect(() => {
    const handleResize = () => {
      if (createTextareaRef.current && step === 'create') {
        // Use setTimeout to ensure the resize is complete
        setTimeout(() => {
          autoResizeTextarea(createTextareaRef.current as any);
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [step]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Toast helper functions
  const showLoadingToastMessage = (message: string) => {
    setLoadingMessage(message);
    setShowLoadingToast(true);
  };

  const showSuccessToastMessage = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };

  const showErrorToastMessage = (message: string) => {
    setErrorMessage(message);
    setShowErrorToast(true);
    setTimeout(() => setShowErrorToast(false), 4000);
  };

  // Get display name for content type
  const getContentTypeDisplayName = (type: ContentType) => {
    switch (type) {
      case 'book': return 'Book';
      case 'podcast': return 'Podcast';
      case 'movie': return 'Movie/TV Show';
      case 'article': return 'Article';
      case 'pdf': return 'Upload';
      default: return 'Book';
    }
  };

  // Handler for content type change
  const handleContentTypeChange = (newType: ContentType) => {
    setContentType(newType);
    setShowDropdown(false);
    setError('');
    // Reset search state
    setSearchQuery('');
    setSearchResults([]);
    setSelectedContent(null);
    setMovieResults([]);
    setSelectedMovie(null);
    setArticleUrl('');
    setArticleMetadata(null);
    setShowManualArticleInput(false);
    setManualArticleTitle('');
    setPdfFile(null);
    setHasSearched(false);
    setIsSearching(false);
    // Focus appropriate input
    if (newType === 'article') {
      setTimeout(() => articleInputRef.current?.focus(), 0);
    } else if (newType === 'book' || newType === 'podcast' || newType === 'movie') {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);


  // Unified content search service (books + podcasts)
  const searchContent = async (query: string, type: 'book' | 'podcast') => {
    if (type === 'book') {
      const response = await api.get(`/books/search?q=${encodeURIComponent(query)}`);
      return response.data.books;
    } else {
      const response = await api.get(`/podcasts/search?q=${encodeURIComponent(query)}`);
      return response.data.podcasts;
    }
  };

  // Fetch article metadata from URL
  const fetchArticleMetadata = async (url: string): Promise<ArticleMetadata | null> => {
    const response = await api.post('/articles/fetch-metadata', { url });
    return response.data.metadata;
  };

  // Upload PDF file
  const uploadPDF = async (file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('pdf', file);

      const response = await api.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.fileUrl;
    } catch (error) {
      return null;
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
    setError('');
  };


  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear any errors when user starts typing
    setError('');

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Show loading while typing
    if (query.trim().length > 0) {
      setIsSearching(true);

      // Search after user stops typing for 500ms
      const newTimeout = setTimeout(() => {
        performSearch(query);
      }, 500);
      setSearchTimeout(newTimeout);
    } else {
      // Clear results when input is empty
      setSearchResults([]);
      setHasSearched(false);
      setIsSearching(false);
    }
  };

  const performSearch = async (query: string) => {
    setIsSearching(true);
    setHasSearched(false); // Reset to show loading spinner
    setError('');
    setSelectedContent(null);
    setSelectedMovie(null);

    try {
      if (contentType === 'movie') {
        // Search movies using OMDB API
        const response = await api.get(`/movies/search?q=${encodeURIComponent(query)}`);
        setMovieResults(response.data);
        setHasSearched(true);
      } else {
        // Search books/podcasts
        const results = await searchContent(query, contentType as 'book' | 'podcast');
        setSearchResults(results);
        setHasSearched(true);
      }
      if ((contentType === 'movie' ? movieResults : searchResults).length === 0) {
        // No results found
      }
    } catch (err: any) {
      // Always just show empty results with "No results found" message
      // Don't show error messages for search failures
      setError('');
      if (contentType === 'movie') {
        setMovieResults([]);
      } else {
        setSearchResults([]);
      }
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  // Since we now have separate searches, no need to filter
  const filteredResults = searchResults;

  const handleContentSelect = (content: ContentSearchResult) => {
    setSelectedContent(content);
    setStep('create');
  };

  const handleArticleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!articleUrl.trim()) return;

    setIsFetchingMetadata(true);
    setError('');

    try {
      const metadata = await fetchArticleMetadata(articleUrl);
      if (metadata) {
        setArticleMetadata(metadata);
        setStep('create');
      } else {
        setError('Could not fetch article metadata. Please check the URL and try again.');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch article metadata';

      // If it's a bot protection error or any error, show manual input option
      if (err.response?.status === 403 || err.response?.status === 429 || err.response?.status >= 400) {
        setShowManualArticleInput(true);
        setError(`${errorMessage} Please enter the article title manually below.`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsFetchingMetadata(false);
    }
  };

  const handleManualArticleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualArticleTitle.trim()) return;

    // Create article metadata with manual title and no image
    setArticleMetadata({
      title: manualArticleTitle.trim(),
      url: articleUrl,
      description: '',
      image: '', // No image for manual entries
      author: '',
      publishedDate: ''
    });
    setStep('create');
  };

  const handlePDFUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear any previous errors when user selects a file
    setError('');

    // Validate file type
    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('PDF file must be smaller than 10MB.');
      return;
    }

    // Store the file locally without uploading yet
    setPdfFile({
      name: file.name,
      size: file.size,
      type: file.type,
      file: file // Store the actual file object
    });
    setStep('create');
  };

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPosting) return;

    // Validate that content is selected
    if ((contentType === 'book' || contentType === 'podcast') && !selectedContent) {
      showErrorToastMessage('Please select a book or podcast first');
      return;
    }
    if (contentType === 'movie' && !selectedMovie) {
      showErrorToastMessage('Please select a movie first');
      return;
    }
    if (contentType === 'article' && !articleMetadata) {
      showErrorToastMessage('Please fetch article metadata first');
      return;
    }
    if (contentType === 'pdf' && !pdfFile) {
      showErrorToastMessage('Please upload a PDF file first');
      return;
    }

    setIsPosting(true);
    showLoadingToastMessage('Creating post...');
    setError('');

    try {
      let postData: CreatePostData;


      if ((contentType === 'book' || contentType === 'podcast') && selectedContent) {
        // Existing book/podcast

        // Handle different possible author field names
        const author = selectedContent.author || (selectedContent as any).authors || 'Unknown Author';

        postData = {
          content_title: selectedContent.title || 'Untitled',
          content_author: author,
          cover_image_url: selectedContent.image || undefined,
          content_url: selectedContent.url || undefined,
          post_content: postContent ? sanitizePostHtml(linkifyMentions(postContent)).trim() || undefined : undefined,
          content_type: contentType
        };
      } else if (contentType === 'movie' && selectedMovie) {
        // Movie from OMDB
        // Use writer for TV series, director for movies
        const creator = selectedMovie.type === 'series'
          ? (selectedMovie.writer || 'Creator N/A')
          : (selectedMovie.director || 'Director N/A');

        postData = {
          content_title: `${selectedMovie.title} (${selectedMovie.year})`,
          content_author: creator,
          cover_image_url: selectedMovie.poster || undefined,
          content_url: selectedMovie.imdbID ? `https://www.imdb.com/title/${selectedMovie.imdbID}` : undefined,
          post_content: postContent ? sanitizePostHtml(linkifyMentions(postContent)).trim() || undefined : undefined,
          content_type: 'movie',
          imdb_rating: selectedMovie.imdbRating || undefined,
          imdb_votes: selectedMovie.imdbVotes || undefined
        };
      } else if (contentType === 'article' && articleMetadata) {
        // Article from URL
        postData = {
          content_title: articleMetadata.title,
          content_author: articleMetadata.author || 'Unknown Author',
          cover_image_url: articleMetadata.image || undefined,
          content_url: articleMetadata.url,
          post_content: postContent ? sanitizePostHtml(linkifyMentions(postContent)).trim() || undefined : undefined,
          content_type: 'article',
          image_width: articleMetadata.imageWidth || undefined,
          image_height: articleMetadata.imageHeight || undefined
        };
      } else if (contentType === 'pdf' && pdfFile) {
        // PDF upload - upload the file first, then create the post
        if (!pdfFile.file) {
          throw new Error('PDF file not found');
        }

        showLoadingToastMessage('Uploading PDF...');
        const fileUrl = await uploadPDF(pdfFile.file);

        if (!fileUrl) {
          throw new Error('Failed to upload PDF');
        }

        showLoadingToastMessage('Creating post...');
        postData = {
          content_title: pdfFile.name.replace('.pdf', ''),
          content_author: 'PDF Document',
          cover_image_url: undefined,
          content_url: fileUrl,
          post_content: postContent ? sanitizePostHtml(linkifyMentions(postContent)).trim() || undefined : undefined,
          content_type: 'pdf'
        };
      } else {
        throw new Error('Please select content before creating a post');
      }

      const response = await api.post('/posts', postData);
      const newPost = response.data.post;

      setShowLoadingToast(false);
      showSuccessToastMessage('Post created successfully!');

      // Navigate to post page after a short delay
      setTimeout(() => {
        navigate(`/post/${newPost.id}`);
      }, 1500);

    } catch (err: any) {
      setShowLoadingToast(false);
      // Show generic error message to user
      showErrorToastMessage('Failed to create post');
    } finally {
      setIsPosting(false);
    }
  };


  const goBack = () => {
    // Clear any errors when navigating back
    setError('');

    if (step === 'create') {
      setStep('search');
    } else {
      // Navigate back to feed
      navigate(-1);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="create-page">
      <div className="create-container">
        <div className="create-header">
          <div className="create-header-left">
            {step === 'create' && (
              <button onClick={goBack} className="create-back-button">
                <ArrowLeft className="create-back-icon" />
              </button>
            )}
          </div>
          <h3 className="create-title">Share your thoughts</h3>
          <div className="create-header-right"></div>
        </div>

        <div className="create-body">
          <div className="create-main">

            {/* Content Type Toggle - Hidden when creating post */}
            {step !== 'create' && (
              <div className="create-content-type-selection">
                {/* Custom Dropdown for mobile */}
                <div className="create-content-type-dropdown-container" ref={dropdownRef}>
                  <button
                    className="create-content-type-dropdown-button"
                    onClick={() => setShowDropdown(!showDropdown)}
                  >
                    <span className="create-content-type-dropdown-text">
                      {getContentTypeDisplayName(contentType)}
                    </span>
                    <ChevronDown className={`create-content-type-dropdown-icon ${showDropdown ? 'open' : ''}`} />
                  </button>

                  {showDropdown && (
                    <div className="create-content-type-dropdown-menu">
                      <button
                        className={`create-content-type-dropdown-item ${contentType === 'book' ? 'active' : ''}`}
                        onClick={() => handleContentTypeChange('book')}
                      >
                        <span>Book</span>
                        {contentType === 'book' && <Check className="create-content-type-dropdown-check" />}
                      </button>
                      <button
                        className={`create-content-type-dropdown-item ${contentType === 'podcast' ? 'active' : ''}`}
                        onClick={() => handleContentTypeChange('podcast')}
                      >
                        <span>Podcast</span>
                        {contentType === 'podcast' && <Check className="create-content-type-dropdown-check" />}
                      </button>
                      <button
                        className={`create-content-type-dropdown-item ${contentType === 'movie' ? 'active' : ''}`}
                        onClick={() => handleContentTypeChange('movie')}
                      >
                        <span>Movie/TV Show</span>
                        {contentType === 'movie' && <Check className="create-content-type-dropdown-check" />}
                      </button>
                      <button
                        className={`create-content-type-dropdown-item ${contentType === 'article' ? 'active' : ''}`}
                        onClick={() => handleContentTypeChange('article')}
                      >
                        <span>Article</span>
                        {contentType === 'article' && <Check className="create-content-type-dropdown-check" />}
                      </button>
                      <button
                        className={`create-content-type-dropdown-item ${contentType === 'pdf' ? 'active' : ''}`}
                        onClick={() => handleContentTypeChange('pdf')}
                      >
                        <span>Upload</span>
                        {contentType === 'pdf' && <Check className="create-content-type-dropdown-check" />}
                      </button>
                    </div>
                  )}
                </div>

                {/* Radio buttons for desktop */}
                <div className="radio-inputs">
                  <label className="radio">
                    <input
                      type="radio"
                      name="contentType"
                      value="book"
                      checked={contentType === 'book'}
                      onChange={(e) => {
                        setContentType('book');
                        setError(''); // Clear any previous errors
                        // Reset search state when switching content types
                        setSearchQuery('');
                        setSearchResults([]);
                        setSelectedContent(null);
                        setHasSearched(false);
                        setIsSearching(false);
                        // Focus search input
                        setTimeout(() => searchInputRef.current?.focus(), 0);
                      }}
                    />
                    <span className="radio-item">
                      <span>Book</span>
                    </span>
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      name="contentType"
                      value="podcast"
                      checked={contentType === 'podcast'}
                      onChange={(e) => {
                        setContentType('podcast');
                        setError(''); // Clear any previous errors
                        // Reset search state when switching content types
                        setSearchQuery('');
                        setSearchResults([]);
                        setSelectedContent(null);
                        setHasSearched(false);
                        setIsSearching(false);
                        // Focus search input
                        setTimeout(() => searchInputRef.current?.focus(), 0);
                      }}
                    />
                    <span className="radio-item">
                      <span>Podcast</span>
                    </span>
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      name="contentType"
                      value="movie"
                      checked={contentType === 'movie'}
                      onChange={(e) => {
                        setContentType('movie');
                        setError(''); // Clear any previous errors
                        // Reset search state when switching content types
                        setSearchQuery('');
                        setMovieResults([]);
                        setSelectedMovie(null);
                        setHasSearched(false);
                        setIsSearching(false);
                        // Focus search input
                        setTimeout(() => searchInputRef.current?.focus(), 0);
                      }}
                    />
                    <span className="radio-item">
                      <span>Movie/TV Show</span>
                    </span>
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      name="contentType"
                      value="article"
                      checked={contentType === 'article'}
                      onChange={(e) => {
                        setContentType('article');
                        setError(''); // Clear any previous errors
                        // Reset article state when switching content types
                        setArticleUrl('');
                        setArticleMetadata(null);
                        setShowManualArticleInput(false);
                        setManualArticleTitle('');
                        // Focus article URL input
                        setTimeout(() => articleInputRef.current?.focus(), 0);
                      }}
                    />
                    <span className="radio-item">
                      <span>Article</span>
                    </span>
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      name="contentType"
                      value="pdf"
                      checked={contentType === 'pdf'}
                      onChange={(e) => {
                        setContentType('pdf');
                        setError(''); // Clear any previous errors
                        // Reset PDF state when switching content types
                        setPdfFile(null);
                      }}
                    />
                    <span className="radio-item">
                      <span>Upload</span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Step 2: Search/Input based on content type */}
            {step === 'search' && (
              <>
                {(contentType === 'book' || contentType === 'podcast' || contentType === 'movie') && (
                  <>
                    {/* Search Form for existing content */}
                    <div className="create-search-form">
                      <div className="create-search-section">
                        <div className="create-search-input-container">
                          <div className="create-search-input-wrapper">
                            <input
                              ref={searchInputRef}
                              type="text"
                              value={searchQuery}
                              onChange={handleSearchInput}
                              placeholder={
                                contentType === 'book' ? "Search for a book..." :
                                  contentType === 'podcast' ? "Search for a podcast..." :
                                    "Search for a movie or TV show..."
                              }
                              className="create-search-input"
                            />
                            {searchQuery && (
                              <button
                                onClick={handleClearSearch}
                                className="create-search-clear-button"
                                type="button"
                              >
                                <XMarkIcon className="create-clear-icon" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {error && <div className="create-error-message">{error}</div>}
                    </div>

                    {/* Loading Spinner */}
                    {isSearching && searchQuery.trim().length > 0 && !hasSearched && (
                      <div className="create-loading">
                        <div className="create-loading-spinner"></div>
                        <p className="create-loading-text">
                          {contentType === 'movie' ? 'Searching movies...' : 'Searching...'}
                        </p>
                      </div>
                    )}

                    {/* Search Results */}
                    {hasSearched && (
                      <div className="create-content-selection">
                        <div className="create-results-header">
                          <h3>Results</h3>
                        </div>
                        <div className="create-search-results">
                          {contentType === 'movie' ? (
                            // Movie results
                            <>
                              {movieResults.map((movie, index) => (
                                <div
                                  key={movie.imdbID || index}
                                  className="create-content-result"
                                  onClick={() => {
                                    setSelectedMovie(movie);
                                    setStep('create');
                                  }}
                                >
                                  {movie.poster ? (
                                    <img
                                      src={movie.poster}
                                      alt=""
                                      className="create-content-cover create-content-cover-book"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const placeholder = target.nextElementSibling as HTMLElement;
                                        if (placeholder) placeholder.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  {!movie.poster || true ? (
                                    <div
                                      className="create-cover-placeholder"
                                      style={{ display: movie.poster ? 'none' : 'flex' }}
                                    >
                                      <Film className="create-cover-placeholder-icon" />
                                    </div>
                                  ) : null}
                                  <div className="create-content-info">
                                    <h4>{movie.title} ({movie.year})</h4>
                                    <p className="create-content-author">
                                      {movie.type === 'series'
                                        ? (movie.writer ? `Created by ${movie.writer}` : 'Creator N/A')
                                        : (movie.director ? `Directed by ${movie.director}` : 'Director N/A')
                                      }
                                    </p>
                                  </div>
                                </div>
                              ))}
                              {movieResults.length === 0 && (
                                <div className="create-no-results">
                                  <p>No movies found. Try a different search term.</p>
                                </div>
                              )}
                            </>
                          ) : (
                            // Book/Podcast results
                            <>
                              {filteredResults.map((content, index) => (
                                <div
                                  key={index}
                                  className={`create-content-result ${content.type === 'podcast' ? 'create-podcast-result' : ''}`}
                                  onClick={() => handleContentSelect(content)}
                                >
                                  {content.image ? (
                                    <img
                                      src={content.image}
                                      alt=""
                                      className={`create-content-cover ${content.type === 'podcast' ? 'create-podcast-cover' : 'create-content-cover-book'}`}
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const placeholder = target.nextElementSibling as HTMLElement;
                                        if (placeholder) placeholder.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  {!content.image || true ? (
                                    <div
                                      className="create-cover-placeholder"
                                      style={{ display: content.image ? 'none' : 'flex' }}
                                    >
                                      {content.type === 'book' ? (
                                        <BookOpen className="create-cover-placeholder-icon" />
                                      ) : (
                                        <Mic className="create-cover-placeholder-icon" />
                                      )}
                                    </div>
                                  ) : null}
                                  <div className="create-content-info">
                                    <h4>{decodeHtmlEntities(content.title)}</h4>
                                    <p className="create-content-author">
                                      {decodeHtmlEntities(content.author)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                              {searchResults.length === 0 && (
                                <div className="create-no-results">
                                  <p>No {contentType}s found. Try a different search term.</p>
                                </div>
                              )}
                            </>
                          )}

                        </div>
                      </div>
                    )}
                  </>
                )}

                {contentType === 'article' && (
                  <div className="create-article-input">
                    <form onSubmit={handleArticleUrlSubmit}>
                      <div className="create-url-input-container">
                        <input
                          ref={articleInputRef}
                          type="url"
                          value={articleUrl}
                          onChange={(e) => {
                            setArticleUrl(e.target.value);
                            setError(''); // Clear any errors when user starts typing
                            setShowManualArticleInput(false); // Hide manual input when URL changes
                          }}
                          placeholder="Enter Article URL"

                          className="create-url-input"
                          required
                        />
                        {!showManualArticleInput && (
                          <button
                            type="submit"
                            className="create-url-submit-button"
                            disabled={!articleUrl.trim() || isFetchingMetadata}
                          >
                            {isFetchingMetadata ? 'Searching...' : 'Enter'}
                          </button>
                        )}
                      </div>
                    </form>

                    {showManualArticleInput && (
                      <div className="create-manual-article-input">
                        <form onSubmit={handleManualArticleSubmit}>
                          <div className="create-manual-input-container">
                            <input
                              type="text"
                              value={manualArticleTitle}
                              onChange={(e) => {
                                setManualArticleTitle(e.target.value);
                                setError(''); // Clear any errors when user starts typing
                              }}
                              placeholder="Enter the article title..."
                              className="create-manual-title-input"
                              required
                            />
                            <button
                              type="submit"
                              className="create-manual-submit-button"
                              disabled={!manualArticleTitle.trim()}
                            >
                              Continue
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {contentType === 'pdf' && (
                  <div className="create-pdf-upload">
                    <div className="create-file-upload-area">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handlePDFUpload}
                        className="create-file-input"
                        id="pdf-upload"
                        disabled={isUploadingPdf}
                      />
                      <label htmlFor="pdf-upload" className="create-file-upload-label">
                        <Upload className="create-upload-icon" />
                        <span>
                          {isUploadingPdf ? 'Uploading...' : 'Click to upload PDF'}
                        </span>
                        <p>Maximum file size: 10MB</p>
                      </label>
                    </div>
                    {error && <div className="create-error-message">{error}</div>}
                  </div>
                )}
              </>
            )}

            {/* Step 3: Create Post Form - PostCard Style */}
            {step === 'create' && (
              <form onSubmit={handleCreatePost} className="create-create-form">
                {/* PostCard-style container */}
                <div className="create-postcard-container">
                  {/* User Header */}
                  <div className="create-postcard-header">
                    <div className="create-postcard-user-info">
                      <div className="create-postcard-user-avatar">
                        {user?.profile_picture_url ? (
                          <img
                            src={user.profile_picture_url}
                            alt={`${user?.username || 'User'}'s profile`}
                            className="create-postcard-user-avatar-image"
                            loading="lazy"
                          />
                        ) : (
                          <UserRound className="create-postcard-user-avatar-icon" />
                        )}
                      </div>
                      <div className="create-postcard-user-details">
                        <span className="create-postcard-username">@{user?.username || 'user'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content Info - PostCard Style */}
                  <div className="create-postcard-content-info">
                    <div className="create-postcard-content-header">
                      {/* Book/Podcast/Movie Cover */}
                      {(contentType === 'book' || contentType === 'podcast') && selectedContent && selectedContent.image && (
                        <img
                          src={selectedContent.image}
                          alt={selectedContent.title}
                          className={`create-postcard-cover-image ${selectedContent.type === 'podcast' ? 'create-postcard-cover-image-podcast' : 'create-postcard-cover-image-book'}`}
                        />
                      )}

                      {contentType === 'movie' && selectedMovie && (
                        selectedMovie.poster ? (
                          <img
                            src={selectedMovie.poster}
                            alt={selectedMovie.title}
                            className="create-postcard-cover-image create-postcard-cover-image-movie"
                          />
                        ) : (
                          <div className="create-postcard-cover-placeholder">
                            <Film className="create-postcard-cover-placeholder-icon" />
                          </div>
                        )
                      )}

                      {contentType === 'article' && articleMetadata && articleMetadata.image && (
                        <img
                          src={articleMetadata.image}
                          alt={articleMetadata.title}
                          className="create-postcard-cover-image create-postcard-cover-image-article"
                          style={articleMetadata.imageWidth && articleMetadata.imageHeight ? {
                            aspectRatio: `${articleMetadata.imageWidth} / ${articleMetadata.imageHeight}`
                          } : undefined}
                        />
                      )}

                      <div className="create-postcard-content-details">
                        {/* Title */}
                        <h4 className="create-postcard-content-title">
                          {contentType === 'book' || contentType === 'podcast' ? (
                            decodeHtmlEntities(selectedContent?.title || '')
                          ) : contentType === 'movie' ? (
                            `${selectedMovie?.title || ''} (${selectedMovie?.year || ''})`
                          ) : contentType === 'article' ? (
                            decodeHtmlEntities(articleMetadata?.title || '')
                          ) : contentType === 'pdf' ? (
                            pdfFile?.name || ''
                          ) : ''}
                        </h4>

                        {/* Author/Creator */}
                        {((contentType === 'book' || contentType === 'podcast') && selectedContent?.author) && (
                          <p className="create-postcard-content-author">
                            by {decodeHtmlEntities(selectedContent.author)}
                          </p>
                        )}

                        {contentType === 'movie' && selectedMovie && (
                          <>
                            <p className="create-postcard-content-author">
                              by {selectedMovie.type === 'series'
                                ? (selectedMovie.writer || 'Creator N/A')
                                : (selectedMovie.director || 'Director N/A')
                              }
                            </p>
                            {selectedMovie.imdbRating && (
                              <div className="create-postcard-imdb-rating">
                                <span className="create-postcard-imdb-label">IMDb Rating:</span>
                                <span className="create-postcard-imdb-score">{selectedMovie.imdbRating}/10</span>
                                {selectedMovie.imdbVotes && (
                                  <span className="create-postcard-imdb-votes">({selectedMovie.imdbVotes} votes)</span>
                                )}
                              </div>
                            )}
                          </>
                        )}

                        {contentType === 'article' && articleMetadata?.author && (
                          <p className="create-postcard-content-author">
                            by {decodeHtmlEntities(articleMetadata.author)}
                          </p>
                        )}

                        {contentType === 'pdf' && pdfFile && (
                          <p className="create-postcard-content-author">
                            PDF Document
                          </p>
                        )}

                        {/* Action Button */}
                        <button
                          type="button"
                          className="create-postcard-content-redirect-button"
                          onClick={(e) => {
                            e.preventDefault();
                            // Handle content redirect based on type
                            if (contentType === 'book' && selectedContent) {
                              const searchQuery = `${decodeHtmlEntities(selectedContent.title)} ${decodeHtmlEntities(selectedContent.author)}`;
                              window.open(`https://www.amazon.com/s?k=${encodeURIComponent(searchQuery)}`, '_blank');
                            } else if (contentType === 'podcast' && selectedContent?.url) {
                              window.open(selectedContent.url, '_blank');
                            } else if (contentType === 'movie' && selectedMovie) {
                              const url = selectedMovie.imdbID ? `https://www.imdb.com/title/${selectedMovie.imdbID}` : '';
                              if (url) window.open(url, '_blank');
                            } else if (contentType === 'article' && articleMetadata?.url) {
                              window.open(articleMetadata.url, '_blank');
                            } else if (contentType === 'pdf' && pdfFile) {
                              // PDF will be uploaded on post creation
                            }
                          }}
                        >
                          {contentType === 'book' ? (
                            <>
                              Read Now
                              <BookOpen className="create-postcard-content-type-icon" />
                            </>
                          ) : contentType === 'podcast' ? (
                            <>
                              Watch Now
                              <Mic className="create-postcard-content-type-icon" />
                            </>
                          ) : contentType === 'movie' ? (
                            <>
                              View
                              <Film className="create-postcard-content-type-icon" />
                            </>
                          ) : contentType === 'article' ? (
                            <>
                              Read Article
                              <FileText className="create-postcard-content-type-icon" />
                            </>
                          ) : contentType === 'pdf' ? (
                            <>
                              Open
                              <FileText className="create-postcard-content-type-icon" />
                            </>
                          ) : (
                            <>
                              View
                              <BookOpen className="create-postcard-content-type-icon" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* User's thoughts textarea */}
                  <div
                    className="create-postcard-user-content"
                    onClick={() => {
                      // Keep focus on textarea when clicking anywhere in the container
                      if (createTextareaRef.current) {
                        createTextareaRef.current.focus();
                      }
                    }}
                  >
                    <div
                      ref={createTextareaRef}
                      id="content"
                      contentEditable
                      suppressContentEditableWarning
                      onInput={(e) => {
                        const div = e.currentTarget;
                        const text = div.innerText;

                        // Update character count
                        setCharacterCount(text.length);

                        // If text is empty, clear all HTML including <br> tags
                        if (text.trim() === '') {
                          div.innerHTML = '';
                          setPostContent('');
                          setCharacterCount(0);
                          div.classList.add('empty');
                          autoResizeTextarea(div as any);
                          return;
                        }

                        // Don't sanitize during input - let the user see the content as-is
                        // Sanitization will happen when they click post
                        setPostContent(div.innerHTML);

                        // Update placeholder visibility
                        div.classList.remove('empty');

                        autoResizeTextarea(div as any);
                      }}
                      onPaste={(e) => {
                        e.preventDefault();

                        // Get HTML content from clipboard
                        const html = e.clipboardData.getData('text/html');
                        const text = e.clipboardData.getData('text/plain');

                        // Use HTML if available, otherwise convert plain text newlines to <br>
                        let contentToPaste;
                        if (html) {
                          contentToPaste = html;
                        } else {
                          // Convert plain text newlines to <br> tags
                          contentToPaste = text.replace(/\n/g, '<br>');
                        }

                        // Don't sanitize during paste - let the user see the content as-is
                        // Sanitization will happen when they click post

                        // Insert at cursor position
                        const selection = window.getSelection();
                        if (selection && selection.rangeCount > 0) {
                          const range = selection.getRangeAt(0);
                          range.deleteContents();

                          // Insert as HTML
                          range.insertNode(document.createRange().createContextualFragment(contentToPaste));

                          // Move cursor to end of pasted content
                          range.collapse(false);
                          selection.removeAllRanges();
                          selection.addRange(range);

                          // Update state and character count
                          const currentDiv = e.currentTarget;
                          const currentText = currentDiv.innerText;
                          setCharacterCount(currentText.length);
                          setPostContent(sanitizePostHtml(currentDiv.innerHTML));
                          autoResizeTextarea(currentDiv as any);

                          // Ensure cursor is at the end after paste
                          setTimeout(() => {
                            const newSelection = window.getSelection();
                            if (newSelection) {
                              const newRange = document.createRange();
                              newRange.selectNodeContents(currentDiv);
                              newRange.collapse(false);
                              newSelection.removeAllRanges();
                              newSelection.addRange(newRange);
                            }
                          }, 0);
                        }
                      }}
                      onBlur={(e) => {
                        // Check if the related target (element being focused) is the submit button
                        const relatedTarget = e.relatedTarget as HTMLButtonElement;
                        const isClickingButton = relatedTarget?.type === 'submit';

                        // Only refocus if not clicking the submit button and not clicking outside the form
                        if (!isClickingButton && !relatedTarget) {
                          setTimeout(() => {
                            if (createTextareaRef.current && step === 'create') {
                              // Preserve cursor position when refocusing
                              const selection = window.getSelection();
                              if (selection && selection.rangeCount > 0) {
                                const range = selection.getRangeAt(0);
                                createTextareaRef.current.focus();
                                selection.removeAllRanges();
                                selection.addRange(range);
                              } else {
                                createTextareaRef.current.focus();
                              }
                            }
                          }, 0);
                        }
                      }}
                      data-placeholder="Share your thoughts..."
                      className="create-postcard-textarea"
                      role="textbox"
                      aria-multiline="true"
                    />
                  </div>

                  {/* Post button (replaces action buttons) */}
                  <div className="create-postcard-actions">
                    {/* Character counter - only show when over limit */}
                    {characterCount > 1000 ? (
                      <div className="create-character-counter">
                        <span className="create-character-count over-limit">
                          -{characterCount - 1000}
                        </span>
                      </div>
                    ) : (
                      <div></div>
                    )}

                    <button
                      type="submit"
                      className={`create-postcard-post-button ${!createTextareaRef.current?.innerText?.trim() || isPosting || characterCount > 1000 ? 'disabled' : ''}`}
                      disabled={!createTextareaRef.current?.innerText?.trim() || isPosting || characterCount > 1000}
                      onMouseDown={(e) => {
                        // Prevent the textarea from losing focus when clicking the button
                        e.preventDefault();
                      }}
                      onClick={(e) => {
                        // Manually trigger form submission
                        if (!createTextareaRef.current?.innerText?.trim() || isPosting || characterCount > 1000) return;
                        handleCreatePost(e as any);
                      }}
                    >
                      {isPosting ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                </div>
              </form>
            )}

          </div>
        </div>
      </div>

      {/* Universal Toasts */}
      <UniversalToast type="loading" message={loadingMessage} show={showLoadingToast} />
      <UniversalToast type="success" message={successMessage} show={showSuccessToast} />
      <UniversalToast type="error" message={errorMessage} show={showErrorToast} />
    </div>
  );
};

export default Create;

